import supabase from '../lib/supabase';

export const teamService = {
  // Test connection to team members table
  async testConnection() {
    try {
      const { data, error } = await supabase
        .from('team_members_bp2025')
        .select('count')
        .limit(1);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Create a new team member (Admin only)
  async createTeamMember(memberData, createdBy) {
    try {
      const newMember = {
        username: memberData.username,
        password_hash: memberData.password, // In production, this should be hashed
        full_name: memberData.fullName,
        email: memberData.email,
        role: memberData.role || 'team_member',
        is_active: memberData.isActive !== undefined ? memberData.isActive : true,
        created_by: createdBy,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('team_members_bp2025')
        .insert([newMember])
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get all team members
  async getAllTeamMembers() {
    try {
      const { data, error } = await supabase
        .from('team_members_bp2025')
        .select(`
          id,
          username,
          full_name,
          email,
          role,
          is_active,
          created_at,
          updated_at,
          admin_users_bp2025!created_by (
            full_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update team member details (Admin only)
  async updateTeamMember(memberId, updateData) {
    try {
      const updateFields = {
        updated_at: new Date().toISOString()
      };

      // Only update provided fields
      if (updateData.fullName) updateFields.full_name = updateData.fullName;
      if (updateData.email) updateFields.email = updateData.email;
      if (updateData.role) updateFields.role = updateData.role;
      if (updateData.isActive !== undefined) updateFields.is_active = updateData.isActive;

      const { data, error } = await supabase
        .from('team_members_bp2025')
        .update(updateFields)
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update team member password (Admin only)
  async updateTeamMemberPassword(memberId, newPassword) {
    try {
      const { data, error } = await supabase
        .from('team_members_bp2025')
        .update({
          password_hash: newPassword, // In production, this should be hashed
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Delete team member (Admin only)
  async deleteTeamMember(memberId) {
    try {
      const { data, error } = await supabase
        .from('team_members_bp2025')
        .delete()
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Team member login
  async login(username, password) {
    try {
      const { data: teamMember, error } = await supabase
        .from('team_members_bp2025')
        .select('*')
        .eq('username', username)
        .eq('is_active', true)
        .single();

      if (error) {
        return { success: false, error: 'Invalid credentials' };
      }

      if (!teamMember) {
        return { success: false, error: 'Invalid credentials' };
      }

      // Validate password
      const isValidPassword = password === teamMember.password_hash;

      if (!isValidPassword) {
        return { success: false, error: 'Invalid credentials' };
      }

      // Store team member session in localStorage
      const sessionData = {
        id: teamMember.id,
        username: teamMember.username,
        fullName: teamMember.full_name,
        email: teamMember.email,
        role: teamMember.role,
        loginTime: new Date().toISOString(),
        userType: 'team_member'
      };

      localStorage.setItem('teamMemberSession', JSON.stringify(sessionData));
      
      return { success: true, user: sessionData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check if team member is logged in
  isAuthenticated() {
    const session = localStorage.getItem('teamMemberSession');
    return !!session;
  },

  // Get current team member session
  getCurrentUser() {
    const session = localStorage.getItem('teamMemberSession');
    return session ? JSON.parse(session) : null;
  },

  // Logout team member
  logout() {
    try {
      localStorage.removeItem('teamMemberSession');
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get team member statistics
  async getTeamStats() {
    try {
      const [totalResult, activeResult] = await Promise.all([
        supabase
          .from('team_members_bp2025')
          .select('*', { count: 'exact', head: true }),
        supabase
          .from('team_members_bp2025')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
      ]);

      const stats = {
        totalMembers: totalResult.count || 0,
        activeMembers: activeResult.count || 0,
        inactiveMembers: (totalResult.count || 0) - (activeResult.count || 0)
      };

      return { success: true, data: stats };
    } catch (error) {
      return { 
        success: true,
        data: {
          totalMembers: 0,
          activeMembers: 0,
          inactiveMembers: 0
        }
      };
    }
  },

  // Subscribe to real-time team member changes
  subscribeToChanges(callback) {
    try {
      const subscription = supabase
        .channel('team_members_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'team_members_bp2025'
        }, (payload) => {
          callback(payload);
        })
        .subscribe();

      return subscription;
    } catch (error) {
      return null;
    }
  },

  // Activate/Deactivate team member
  async toggleMemberStatus(memberId, isActive) {
    try {
      const { data, error } = await supabase
        .from('team_members_bp2025')
        .update({
          is_active: isActive,
          updated_at: new Date().toISOString()
        })
        .eq('id', memberId)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};